import { FormInstance } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { BankAccount, Invoice } from '@/lib/types';
import {
  Payment,
  PaymentInvoiceItem,
  PaymentSetting,
} from '@/lib/types/payment';
import { createPaymentInvoices } from '../_helpers/inPaymentPageHelper';

type UseInPaymentInvoiceSyncParams = {
  form: FormInstance;
  bankAccounts: BankAccount[];
  invoices: { data: Invoice[] };
  invoicesIsLoading: boolean;
  paymentSettings: PaymentSetting[];
  paymentSettingsIsLoading: boolean;
  paymentsInProgress: Payment[] | undefined;
  refetchInvoices: () => Promise<unknown>;
  setPayableInvoiceCount: (value: number) => void;
};

export const useInPaymentInvoiceSync = ({
  form,
  bankAccounts,
  invoices,
  invoicesIsLoading,
  paymentSettings,
  paymentSettingsIsLoading,
  paymentsInProgress,
  refetchInvoices,
  setPayableInvoiceCount,
}: UseInPaymentInvoiceSyncParams) => {
  const [unusedCreditInvoices, setUnusedCreditInvoices] = useState<Invoice[]>(
    [],
  );
  const [invoiceIds, setInvoiceIds] = useState<number[]>([]);
  const [checkedList, setCheckedList] = useState<number[]>([]);
  const hadPaymentsInProgressRef = useRef(false);

  const isCheckAll =
    checkedList.length === invoiceIds.length && invoiceIds.length > 0;
  const isIndeterminate =
    checkedList.length > 0 && checkedList.length < invoiceIds.length;
  const noCheckedBoxes = checkedList.length === 0;

  const onCheckAllChange = useCallback(
    (e: { target: { checked: boolean } }) => {
      setCheckedList(e.target.checked ? invoiceIds : []);
    },
    [invoiceIds],
  );

  const onCheck = useCallback((id: number, checked: boolean) => {
    setCheckedList((prev) => {
      if (checked) {
        return prev.includes(id) ? prev : [...prev, id];
      }

      return prev.filter((invoiceId) => invoiceId !== id);
    });
  }, []);

  useEffect(() => {
    if (bankAccounts.length) {
      form.setFieldValue('iban', bankAccounts[0].iban);
    }
  }, [bankAccounts, form]);

  useEffect(() => {
    if (invoicesIsLoading || paymentSettingsIsLoading) {
      return;
    }

    const currentInvoices =
      (form.getFieldValue('invoices') as PaymentInvoiceItem[] | undefined) ??
      [];
    const { paymentInvoices, unusedCreditInvoices: nextUnusedCreditInvoices } =
      createPaymentInvoices(invoices.data, currentInvoices, paymentSettings);
    const nextInvoiceIds = paymentInvoices.map(
      (invoice) => invoice.invoice.invoiceId,
    );

    setPayableInvoiceCount(invoices.data.length);
    setInvoiceIds(nextInvoiceIds);
    setCheckedList(nextInvoiceIds);
    setUnusedCreditInvoices(nextUnusedCreditInvoices);
    form.resetFields();
    form.setFieldValue(['invoices'], paymentInvoices);
  }, [
    form,
    invoices,
    invoicesIsLoading,
    paymentSettings,
    paymentSettingsIsLoading,
    setPayableInvoiceCount,
  ]);

  useEffect(() => {
    const hasPaymentsInProgress = Boolean(paymentsInProgress?.length);

    if (hadPaymentsInProgressRef.current && !hasPaymentsInProgress) {
      void refetchInvoices();
    }

    hadPaymentsInProgressRef.current = hasPaymentsInProgress;
  }, [paymentsInProgress, refetchInvoices]);

  return {
    checkedList,
    invoiceIds,
    unusedCreditInvoices,
    isCheckAll,
    isIndeterminate,
    noCheckedBoxes,
    setCheckedList,
    setInvoiceIds,
    onCheckAllChange,
    onCheck,
  };
};
